import * as ROT from "rot-js"

import { Color, PaletteColor } from "./defines/colors"
import {
  AttackType,
  Direction,
  EnemyType,
  ItemType,
  MapID,
  PlayerAction,
  PopupID,
} from "./defines/enums"
import { handleInput } from "./defines/input"
import {
  Message,
  MusicSpec,
  PigmentDrop,
  Position,
  TapestryStats,
} from "./defines/types"
import {
  DBDefaults,
  EnemyDefaults,
  EngineDefaults,
  Pigments,
} from "./defines/values"
import { BounceAsyncEnemy } from "./entity/enemies/bounce/async"
import { Enemy } from "./entity/enemies/enemy"
import { PathAsyncEnemy } from "./entity/enemies/path/async"
import { Entity } from "./entity/entity"
import { LockedDoor } from "./entity/other/lockeddoor"
import { MapPortal } from "./entity/other/mapportal"
import { PopupTrigger } from "./entity/other/popuptrigger"
import { TapestryMap } from "./map/tapestrymap"
import { MusicPlayer } from "./music/musicplayer"
import { Player } from "./player/player"
import { Archive } from "./popup/archive"
import { Bestiary } from "./popup/bestiary"
import { Loadout } from "./popup/loadout"
import { Popup } from "./popup/popup"
import { Tapestry } from "./popup/tapestry"
import { Collectibles } from "./popup/trophycase"
import { buildArsenal, buildGizmos, buildWardrobe } from "./utils/cabinet"
import { getOutputStringForColor } from "./utils/color"
import { convertToPigment } from "./utils/item"
import { getMapData, makeMap, mapIDToString } from "./utils/map"
import { randomEnemyEssenceDrop, randomEnemyPigmentDrop } from "./utils/misc"
import { displayOptic } from "./utils/player"
import { generateRandomNumberZeroToOne } from "./utils/rng"
import {
  dbRetrievePlayer,
  dbRetrieveTapestry,
  dbStore,
} from "./utils/tapestrydb"
import { Timer } from "./utils/timer"
import {
  appendRandomRoomsTotal,
  closeLoadingPopup,
  enableStartGameButton,
  musicIsMuted,
  musicIsStopped,
  musicVolume,
  openLoadingPopup,
  showPauseButton,
  showPlayButton,
  updateGizmoDisplay,
  updateSongInfo,
} from "./webpage"

export class Engine {
  private messagesDisplay: ROT.Display
  private gameDisplay: ROT.Display
  private statsDisplay: ROT.Display
  private map: TapestryMap
  private player: Player
  private messages: Message[]

  private _clock: number
  private _timer: Timer

  private _updateRequested: boolean
  private _updateTimer: Timer

  private popup: PopupID
  private trophyCases: globalThis.Map<PopupID, Popup>

  private _musicPlayer: MusicPlayer | undefined = undefined

  constructor(
    messagesTarget: HTMLElement,
    gameTarget: HTMLElement,
    statsTarget: HTMLElement,
    public currentMapID: MapID
  ) {
    // MESSAGES DISPLAY
    this.messagesDisplay = new ROT.Display({
      width: EngineDefaults.WIDTH,
      height: EngineDefaults.MESSAGES_HEIGHT,
    })

    let container = this.messagesDisplay.getContainer()!
    messagesTarget.appendChild(container)

    // GAME DISPLAY
    this.gameDisplay = new ROT.Display({
      width: EngineDefaults.WIDTH,
      height: EngineDefaults.GAME_HEIGHT,
    })

    container = this.gameDisplay.getContainer()!
    gameTarget.appendChild(container)

    // STATS DISPLAY
    this.statsDisplay = new ROT.Display({
      width: EngineDefaults.WIDTH,
      height: EngineDefaults.STATS_HEIGHT,
    })

    container = this.statsDisplay.getContainer()!
    statsTarget.appendChild(container)

    this._updateRequested = false
    this._updateTimer = new Timer(EngineDefaults.UPDATE_FREQ)
    this._updateTimer.start(true, () => {
      this.checkUpdateRequests()
    })

    this._clock = 0
    this._timer = new Timer(1000)
    this._timer.start(true, () => {
      this.tickClock()
    })

    // Have to be in order:
    // 1. Init player
    // 2. Init map
    // 3. Place player in map
    // 4. Init displays

    this.player = loadPlayer()

    const mapData = getMapData(currentMapID)
    this.map = makeMap(
      currentMapID,
      mapData,
      this.gameDisplay,
      this.player,
      EngineDefaults.DISPLAY_OFFSETS
    )

    if (mapData.music !== undefined) {
      updateSongInfo(mapData.music.title)
      this.changeMusic(mapData.music, true)
    } else {
      enableStartGameButton()
    }

    window.addEventListener("keydown", (event) => {
      this.update(event)
    })

    this.messages = [
      {
        count: 1,
        message: "You ride the wind into " + mapIDToString(this.currentMapID),
      },
    ]

    this.popup = PopupID.NONE
    this.trophyCases = buildPopups()

    this.map.updateFov()
    this.render()
  }

  getPlayer(): Player {
    return this.player
  }

  private checkUpdateRequests(): void {
    if (this._updateRequested) {
      this.update()
      this._updateRequested = false
    }
  }

  processPlayerAttack(e: Enemy, allowReturnFire: boolean): void {
    const damageDealt = e.takeHit(this.player.dealHit())

    this.sendMessage(
      "You drain " + damageDealt + " essence from " + e.displayName
    )

    if (!e.isAlive()) {
      this.sendMessage("A faint residue remains of " + e.displayName)
      this.sendMessage("You gain " + e.experience + " experience.")
      this.player.addExperience(e.experience)
      this.processEnemyKill(e)
      this.killBeast(e.type)
    } else if (allowReturnFire) {
      this.processEnemyAttack(e, false)
    }

    return
  }

  private processEssenceAttack(amount: number, mob: string): void {
    const essenceLost = this.player.takeHit(amount)
    this.sendMessage("You lose " + essenceLost + " essence to " + mob)
  }

  private processPigmentAttack(amount: number, mob: string): void {
    const pigmentLost = this.player.loseColor(amount)
    this.requestPlayerSaveState()

    if (pigmentLost.length == 0) {
      this.sendMessage(mob + " tries but fails to steal pigment")
    }

    pigmentLost.forEach((p) => {
      this.sendMessage(
        "You lose " + p.amount + " " + p.color + " pigment to " + mob
      )
    })
  }

  processEnemyAttack(e: Enemy, allowReturnFire: boolean): void {
    const attack = e.attack
    const amount = attack.amount
    if (attack.type == AttackType.DRAIN_ESSENCE) {
      this.processEssenceAttack(amount, e.displayName)
    } else if (attack.type == AttackType.DRAIN_PIGMENT) {
      this.processPigmentAttack(amount, e.displayName)
    } else if (attack.type == AttackType.DRAIN_ESSENCE_AND_PIGMENT) {
      this.processEssenceAttack(amount, e.displayName)
      this.processPigmentAttack(amount, e.displayName)
    }
  }

  requestPlayerSaveState(): void {
    dbStore(DBDefaults.KEY_PLAYER, this.player)
  }

  requestUpdate(): void {
    this._updateRequested = true
  }

  showMapSummaryStats(): void {
    const collectibles = this.map.getCollectibleCount()
    const pigments = this.map.getPigmentCount()
    const items = this.map.getItemCount()
    const enemies = this.map.getEnemyCount()

    let message = "Scan reveals - > "
    message += pigments.toString() + " pigment"
    if (pigments != 1) {
      message += "s"
    }
    message += ", "
    message += items.toString() + " item"
    if (items != 1) {
      message += "s"
    }
    message += ", "
    message += enemies.toString() + " enem"
    if (enemies != 1) {
      message += "ies"
    } else {
      message += "y"
    }
    message += ", "
    message += collectibles.toString() + " collectible"
    if (collectibles != 1) {
      message += "s"
    }

    this.sendMessage(message)
  }

  private tickClock(): void {
    this._clock++
    this.renderClock()
  }

  displayRandomRoomsGenerated(rooms: number): void {
    appendRandomRoomsTotal(rooms)
  }

  displayGizmoTimer(output: string): void {
    updateGizmoDisplay(output)
  }

  addBeastToBestiary(type: EnemyType): void {
    const b = this.trophyCases.get(PopupID.BESTIARY) as Bestiary
    if (b !== undefined) {
      b.addBeast(type)
    }
  }

  seeBeast(type: EnemyType): void {
    const b = this.trophyCases.get(PopupID.BESTIARY) as Bestiary
    if (b !== undefined) {
      b.seeBeast(type)
    }
  }

  killBeast(type: EnemyType): void {
    const b = this.trophyCases.get(PopupID.BESTIARY) as Bestiary
    if (b !== undefined) {
      b.killBeast(type)
    }
  }

  audioReady(firstScreen: boolean): void {
    if (this._musicPlayer !== undefined) {
      closeLoadingPopup()
      if (firstScreen) {
        enableStartGameButton()
      }
    } else {
      enableStartGameButton()
    }
  }

  changeMusic(
    config: MusicSpec,
    firstScreen: boolean,
    fromTapestry: boolean = false
  ): void {
    if (config !== undefined) {
      if (this._musicPlayer !== undefined) {
        this._musicPlayer.stop()
        this._musicPlayer.unload()
      }

      if (!fromTapestry) {
        openLoadingPopup()
      }

      updateSongInfo(config.title)
      const volume = musicVolume()
      const muted = musicIsMuted()
      const autoplay = musicIsStopped() ? false : true
      this._musicPlayer = new MusicPlayer(
        config.source,
        firstScreen,
        autoplay,
        volume,
        muted
      )
    }
  }

  muteMusic(): void {
    if (this._musicPlayer !== undefined) {
      this._musicPlayer.mute()
    }
  }

  unmuteMusic(): void {
    if (this._musicPlayer !== undefined) {
      this._musicPlayer.unmute()
    }
  }

  pauseMusic(): void {
    if (this._musicPlayer !== undefined) {
      this._musicPlayer.pause()
    }
  }

  startMusic(): void {
    if (this._musicPlayer !== undefined) {
      this._musicPlayer.play()
    }
  }

  getMusicVolume(): number {
    if (this._musicPlayer !== undefined) {
      return musicVolume()
    }
    return 0.5
  }

  setMusicVolume(volume: number): void {
    if (this._musicPlayer !== undefined) {
      this._musicPlayer.setVolume(volume)
    }
  }

  showPlayButton(): void {
    showPlayButton()
  }

  showPauseButton(): void {
    showPauseButton()
  }

  getEntitiesAtLocation(position: Position): Entity[] {
    // TODO: need entities whose AOE reaches x,y
    return this.map.findEntities(position)
  }

  getNearestInteractables(position: Position, direction: Direction): Entity[] {
    return this.map.findInteractables(position, direction)
  }

  interact(e: Entity) {
    if (e instanceof PopupTrigger) {
      this.popup = e.target
    } else if (e instanceof LockedDoor) {
      if (e.canBeUnlocked()) {
        if (this.player.hasKey(e.target)) {
          this.sendMessage("You unlock the door to " + mapIDToString(e.target))
          this.map.removeTopmostEntity(e.position)
        } else {
          this.sendMessage(
            "You do not have the key for " + mapIDToString(e.target),
            false
          )
        }
      } else {
        this.sendMessage(
          "This door cannot be unlocked until " + e.getUnlockDate(),
          false
        )
      }
    } else if (e instanceof MapPortal) {
      if (this.currentMapID != e.target) {
        this.enterPortal(e.target)
      }
    }
  }

  processEnemyKill(enemy: Enemy): void {
    if (enemy instanceof BounceAsyncEnemy || enemy instanceof PathAsyncEnemy) {
      enemy.stopTimer()
    }

    const drop = generateRandomNumberZeroToOne()

    let essence = 0
    let pigment: PigmentDrop | undefined = undefined

    if (drop < EnemyDefaults.ITEM_DROP_PROB) {
      if (enemy.attack.type == AttackType.DRAIN_ESSENCE) {
        essence = randomEnemyEssenceDrop()
      } else if (enemy.attack.type == AttackType.DRAIN_PIGMENT) {
        pigment = randomEnemyPigmentDrop(this.player.optics)
      } else if (enemy.attack.type == AttackType.DRAIN_ESSENCE_AND_PIGMENT) {
        const half = generateRandomNumberZeroToOne()
        if (half < 0.5) {
          essence = randomEnemyEssenceDrop()
        } else {
          pigment = randomEnemyPigmentDrop(this.player.optics)
        }
      }
    }

    if (essence > 0) {
      this.player.addEssence(essence)
    } else if (pigment && pigment.amount > 0) {
      const converted = convertToPigment(pigment.type)
      this.sendMessage(
        "You collected " + getOutputStringForColor(converted) + " pigment"
      )
      this.player.addPigment(pigment.type, pigment.amount)
    }
  }

  removeEntityAtLocation(position: Position): void {
    this.map.removeTopmostEntity(position)
  }

  removeItemAtLocation(position: Position, type: ItemType): void {
    this.map.removeItem(position, type)
  }

  restart(): void {
    this.map.destroy()

    this.currentMapID = MapID.Portals

    this.sendMessage("You re-form at " + mapIDToString(this.currentMapID))
    this.player.resurrect()

    const mapData = getMapData(this.currentMapID)

    if (mapData.music !== undefined) {
      this.changeMusic(mapData.music, false)
    }

    this.map = makeMap(
      this.currentMapID,
      mapData,
      this.gameDisplay,
      this.player,
      EngineDefaults.DISPLAY_OFFSETS
    )
  }

  sendMessage(msg: string, showRepetition: boolean = true) {
    msg = msg.trim()
    if (msg.length > 0) {
      if (this.messages.length > 0) {
        if (msg == this.messages[0].message) {
          if (showRepetition) {
            this.messages[0].count += 1
          }
        } else {
          this.messages.unshift({ count: 1, message: msg })
        }
      } else {
        this.messages.push({ count: 1, message: msg })
      }

      if (this.messages.length > 6) {
        this.messages = this.messages.slice(0, 6)
      }
    }
  }

  inBounds(x: number, y: number): boolean {
    return this.map.inBounds(x, y)
  }

  isVisible(x: number, y: number): boolean {
    return this.map.isVisible(x, y)
  }

  enterPortal(mapID: MapID): void {
    this.map.destroy()

    this.currentMapID = mapID
    const mapData = getMapData(this.currentMapID)

    if (mapData.music !== undefined) {
      this.changeMusic(mapData.music, false)
    }

    this.map = makeMap(
      this.currentMapID,
      mapData,
      this.gameDisplay,
      this.player,
      EngineDefaults.DISPLAY_OFFSETS
    )

    this.player.visit(this.currentMapID)
    this.sendMessage(
      "You ride the wind into " + mapIDToString(this.currentMapID)
    )
  }

  // TODO: static enemies can be sync enemies with speed=0
  //       has knock-on for this function
  private updateEnemies(updateSyncs: boolean = false): void {
    this.map.placeResidues()
    if (updateSyncs) {
      this.map.bounceSyncEnemies.forEach((e) => {
        const state = e.projectMove()
      })

      this.map.pathSyncEnemies.forEach((e) => {
        const state = e.projectMove()
      })
    }
  }

  private clearDisplays(): void {
    this.messagesDisplay.clear()
    this.gameDisplay.clear()
    this.statsDisplay.clear()
  }

  private update(event?: KeyboardEvent): void {
    if (!event) {
      this.clearDisplays()
      this.updateEnemies()
      if (!this.player.isAlive()) {
        this.restart()
      }
      this.map.updateFov()
      this.render()

      return
    }

    const action = handleInput(event)

    if (this.popup == PopupID.TAPESTRY && action != PlayerAction.NONE) {
      const popup = this.trophyCases.get(this.popup) as Tapestry
      if (popup !== undefined) {
        if (
          action == PlayerAction.MOVE_LEFT &&
          popup.showingConfirmDialog() &&
          popup.canSelectPigment()
        ) {
          popup.changePigmentSelection(Direction.LEFT)
        } else if (
          action == PlayerAction.MOVE_RIGHT &&
          popup.showingConfirmDialog() &&
          popup.canSelectPigment()
        ) {
          popup.changePigmentSelection(Direction.RIGHT)
        } else if (action == PlayerAction.BIND_PIGMENT) {
          popup.showConfirmDialog()
        } else if (
          action == PlayerAction.CONFIRM_YES &&
          popup.canSelectPigment()
        ) {
          popup.hideConfirmDialog()
          const selected = popup.getSelectedPigment()
          popup.bindPigmentToTiles(selected)
          this.player.bindPigment(selected)
          dbStore(DBDefaults.KEY_TAPESTRY, popup)
        } else if (action == PlayerAction.CONFIRM_NO) {
          popup.hideConfirmDialog()
        } else if (action == PlayerAction.CLOSE_POPUP) {
          if (popup.showingConfirmDialog()) {
            popup.hideConfirmDialog()
          } else {
            popup.postRender()
            if (popup.complete) {
              const mapData = getMapData(this.currentMapID)
              if (mapData.music !== undefined) {
                this.changeMusic(mapData.music, false, true)
              }
            }
            this.popup = PopupID.NONE
          }
        }
      }

      this.clearDisplays()
      this.render()

      return
    }

    if (action != PlayerAction.NONE) {
      if (action == PlayerAction.GO_HOME) {
        this.enterPortal(MapID.Portals)
      } else if (
        action == PlayerAction.CLOSE_POPUP &&
        this.popup != PopupID.NONE
      ) {
        this.popup = PopupID.NONE
      } else if (action == PlayerAction.VIEW_LOADOUT) {
        this.popup = PopupID.VIEW_LOADOUT
      } else {
        if (this.popup == PopupID.NONE) {
          this.player.update(action)
        } else if (this.popup != PopupID.TAPESTRY) {
          const popup = this.trophyCases.get(this.popup)
          if (popup !== undefined) {
            popup.update(action)
          }
        }
      }
      this.updateEnemies(this.player.moved)
      if (!this.player.isAlive()) {
        this.restart()
      }

      this.map.updateFov()
      this.clearDisplays()
      this.render()
    }
  }

  private renderClock(): void {
    const clock = this.clockString()
    this.messagesDisplay.drawText(
      EngineDefaults.WIDTH -
        clock.length -
        EngineDefaults.DISPLAY_OFFSETS.xOffset,
      2,
      `%c{${Color.CLOCK}}%b{${Color.BLACK}}${clock}`
    )
  }

  private renderMessages(): void {
    // title
    const title = "Tapestry"
    this.messagesDisplay.drawText(
      EngineDefaults.WIDTH -
        title.length -
        EngineDefaults.DISPLAY_OFFSETS.xOffset,
      1,
      `%c{${Color.PIGMENT_LIGHT_RED}}%b{#000}${title[0]}` +
        `%c{${Color.PIGMENT_LIGHT_YELLOW}}%b{#000}${title[1]}` +
        `%c{${Color.PIGMENT_LIGHT_MAGENTA}}%b{#000}${title[2]}` +
        `%c{${Color.PIGMENT_LIGHT_BLUE}}%b{#000}${title[3]}` +
        `%c{${Color.PIGMENT_LIGHT_CYAN}}%b{#000}${title[4]}` +
        `%c{${Color.PIGMENT_LIGHT_GREEN}}%b{#000}${title[5]}` +
        `%c{${Color.PIGMENT_LIGHT_YELLOW}}%b{#000}${title[6]}` +
        `%c{${Color.PIGMENT_LIGHT_RED}}%b{#000}${title[7]}`
    )

    // Last 3 messages
    for (let i: number = 0; i < this.messages.length && i < 3; ++i) {
      let text = ".".repeat(i) + this.messages[i].message
      let count = this.messages[i].count > 1 ? "x" + this.messages[i].count : ""
      let textColor = i == 0 ? "#fff" : "#808080"

      this.messagesDisplay.drawText(
        1,
        i + 1,
        `%c{${textColor}}%b{#000}${text} ${count}`
      )
    }
  }

  private renderGame(): void {
    if (this.popup == PopupID.NONE) {
      const offset = this.map.render()
      const render = this.player.renderLight()
      this.gameDisplay.drawOver(
        this.player.position.x - offset.xOffset,
        this.player.position.y - offset.yOffset,
        render.symbol,
        render.color,
        null
      )
    } else {
      const tc = this.trophyCases.get(this.popup)
      if (tc) {
        if (tc instanceof Tapestry) {
          tc.display = this.gameDisplay
        }
        tc.prepareToRender()
        tc.render(this.gameDisplay)
      }
    }
  }

  private clockString(): string {
    if (this._clock >= 86400) {
      this._clock = 0
    }

    const hours = String(Math.trunc(this._clock / 3600)).padStart(2, "0")
    const minutes = String(Math.trunc((this._clock % 3600) / 60)).padStart(
      2,
      "0"
    )
    const seconds = String(Math.trunc((this._clock % 3600) % 60)).padStart(
      2,
      "0"
    )

    return hours + ":" + minutes + ":" + seconds
  }

  private renderStats(): void {
    // Location
    const loc = "Location: " + mapIDToString(this.currentMapID)
    this.statsDisplay.drawText(1, 1, `%c{#fff}%b{#000}${loc}`)

    // Gizmo
    const gizmo = "Gizmo: " + this.player.getGizmoStats()
    this.statsDisplay.drawText(
      EngineDefaults.WIDTH / 2,
      1,
      `%c{#fff}%b{#000}${gizmo}`
    )

    // Level and experience
    const level = this.player.level.toString().padStart(3)
    const exp = this.player.experience.toString().padStart(3)
    const nextExp = this.player.getNextLevelExpString()
    const elixirs = this.player.elixirs.toString().padStart(3)
    this.statsDisplay.drawText(
      1,
      2,
      `%c{#fff}%b{#000}Level: ${level}, ` +
        `%c{#fff}%b{#000}Exp: ${exp} ${nextExp}, ` +
        `%c{#fff}%b{#000}Elx: ${elixirs}, ` +
        `%c{#fff}%b{#000}Optic: ` +
        displayOptic(this.player)
    )

    // Main stats
    const playerStats = this.player.getEquipmentStats()
    const eAttack = playerStats.maxEssenceAttack.toString()
    const eDefense = playerStats.essenceDefense.toString()
    const pDefense = playerStats.pigmentDefense.toString()
    const sRange = playerStats.stealthRange.toString()
    this.statsDisplay.drawText(
      1,
      3,
      `%c{#fff}%b{#000}Ess: ${this.player.essence.toString().padStart(3)}, ` +
        `%c{#fff}%b{#000}Atk: ${eAttack.padStart(3)}, ` +
        `%c{#fff}%b{#000}Ess def: ${eDefense.padStart(3)}, ` +
        `%c{#fff}%b{#000}Col def: ${pDefense.padStart(3)}, ` +
        `%c{#fff}%b{#000}Stlh: ${sRange.padStart(3)}`
    )

    // Palette

    let pigmentString: string = ""
    Pigments.forEach((p) => {
      pigmentString += " " + this.getPigmentString(p)
    })

    const binder = this.player.getBinders().toString().padStart(3)
    const binderString = `%c{#fff}%b{#000}Bnd: ${binder}`
    this.statsDisplay.drawText(1, 4, pigmentString + " " + binderString)
  }

  render() {
    this.renderMessages()
    this.renderClock()
    this.renderGame()
    this.renderStats()
  }

  private getPigmentString(color: PaletteColor): string {
    const fg: string = this.getPigmentForeground(color)
    const label: string = this.getPigmentLabel(color)

    return `%c{${fg}}%b{#000}${label}: ${this.player.getColorCountString(
      color
    )}`
  }

  clearTile(x: number, y: number): void {
    this.map.clearTile(x, y, this.gameDisplay, this.player)
  }

  renderEntity(e: Entity): void {
    this.map.renderEntity(e)
  }

  getTapestryStats(): TapestryStats | undefined {
    const tapestry = this.trophyCases.get(PopupID.TAPESTRY) as Tapestry
    if (tapestry !== undefined) {
      return tapestry.getStats()
    }
    return undefined
  }

  private getPigmentForeground(color: PaletteColor): string {
    let fg = "#000"

    switch (color) {
      case PaletteColor.RED:
        fg = "#f00"
        break
      case PaletteColor.GREEN:
        fg = "#0f0"
        break
      case PaletteColor.BLUE:
        fg = "#00f"
        break
      case PaletteColor.YELLOW:
        fg = "#ff0"
        break
      case PaletteColor.MAGENTA:
        fg = "#f0f"
        break
      case PaletteColor.CYAN:
        fg = "#0ff"
        break
    }

    return fg
  }

  private getPigmentLabel(color: PaletteColor): string {
    let label = ""
    switch (color) {
      case PaletteColor.RED:
        label = "Red"
        break
      case PaletteColor.GREEN:
        label = "Grn"
        break
      case PaletteColor.BLUE:
        label = "Blu"
        break
      case PaletteColor.YELLOW:
        label = "Yel"
        break
      case PaletteColor.MAGENTA:
        label = "Mag"
        break
      case PaletteColor.CYAN:
        label = "Cya"
        break
    }

    return label
  }
}

function loadPlayer(): Player {
  const player = new Player({ x: 0, y: 0 })
  const p = dbRetrievePlayer()
  if (p !== undefined) {
    Object.assign(player, p)
  }
  const oSet = new Set<ItemType>(player.optics)
  player.overwriteOptics(Array.from(oSet))
  return player
}

function loadTapestry(): Tapestry {
  const tapestry = new Tapestry(EngineDefaults.DISPLAY_OFFSETS, "Tapestry")
  const t = dbRetrieveTapestry()
  if (t !== undefined) {
    Object.assign(tapestry, t)
  }

  return tapestry
}

function buildPopups(): globalThis.Map<PopupID, Popup> {
  let tc = new globalThis.Map<PopupID, Popup>()

  // Tapestry
  const tapestry = loadTapestry()
  tc.set(PopupID.TAPESTRY, tapestry)
  dbStore

  // Archive
  const archive = new Archive(EngineDefaults.DISPLAY_OFFSETS, "Archive")
  tc.set(PopupID.ARCHIVE, archive)

  // Bestiary
  const bestiary = new Bestiary(EngineDefaults.DISPLAY_OFFSETS, "Bestiary")

  // TODO: remove after testing
  // bestiaryTestData(bestiary)

  tc.set(PopupID.BESTIARY, bestiary)

  // Wardrobe
  const wardrobe = buildWardrobe(
    EngineDefaults.DISPLAY_OFFSETS,
    "Wardrobe",
    "The wardrobe items you have found, and can wear."
  )

  tc.set(PopupID.WARDROBE, wardrobe)

  // Arsenal
  const arsenal = buildArsenal(
    EngineDefaults.DISPLAY_OFFSETS,
    "Arsenal",
    "The weapons you have found, and can wield."
  )
  tc.set(PopupID.ARSENAL, arsenal)

  // Gizmos
  const gizmos = buildGizmos(
    EngineDefaults.DISPLAY_OFFSETS,
    "Gizmos",
    "The gizmos you have found, and can carry on your explorations."
  )
  tc.set(PopupID.GIZMOS, gizmos)

  // Common Finds
  const commonFinds = new Collectibles(
    EngineDefaults.DISPLAY_OFFSETS,
    "Vault of Common Finds",
    Color.PIGMENT_LIGHT_WHITE,
    ItemType.COLLECTIBLE_COMMON
  )
  tc.set(PopupID.COMMON_FINDS, commonFinds)

  // Mythic Finds
  const mythicFinds = new Collectibles(
    EngineDefaults.DISPLAY_OFFSETS,
    "Vault of Mythic Finds",
    Color.PIGMENT_LIGHT_WHITE,
    ItemType.COLLECTIBLE_MYTHIC
  )
  tc.set(PopupID.MYTHIC_FINDS, mythicFinds)

  // Loadout viewer
  const loadout = new Loadout(
    EngineDefaults.DISPLAY_OFFSETS,
    "Equipment Loadout"
  )
  tc.set(PopupID.VIEW_LOADOUT, loadout)

  return tc
}
